import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FeedComposer } from '@/components/feed/FeedComposer';
import { FeedList } from '@/components/feed/FeedList';
import { ReportModal } from '@/components/feed/ReportModal';
import { feedAPI, tokensAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useEntitlements } from '@/contexts/EntitlementsContext';
import { PioneerCard, FounderCard, MentorCard, VirtualBagCard } from '@/components/widgets';
import { CheckinCard } from '@/components/activities';
import { toast } from 'sonner';
import { Flame, ChevronRight, Bot, Users as UsersIcon } from 'lucide-react';
import { PageTransition, HoverCard } from '@/components/motion/Animations';

export function Feed() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPareiActive, loading: entitlementsLoading } = useEntitlements();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportingPostId, setReportingPostId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${baseUrl}/api/admin/me`, {
          headers: { 'Authorization': `Bearer admin_${token}` }
        });
        if (res.ok) setIsAdmin(true);
      } catch {}
    };
    // Simple check: see if admin token exists in localStorage
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) setIsAdmin(true);
  }, []);

  // Load initial posts
  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async (loadMore = false) => {
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      const response = await feedAPI.getPosts(loadMore ? cursor : null);
      const newPosts = response.data;

      if (newPosts.length === 0) {
        setHasMore(false);
      } else {
        setPosts((prev) => (loadMore ? [...prev, ...newPosts] : newPosts));
        
        // Set cursor to the created_at of the last post
        const lastPost = newPosts[newPosts.length - 1];
        setCursor(lastPost.created_at);
      }
    } catch (err) {
      console.error('Failed to load posts:', err);
      setError(err.response?.data?.detail || 'Erro ao carregar posts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async (newPost) => {
    // Add new post to the beginning of the list
    // newPost is already the created post from the FeedComposer
    setPosts((prev) => [newPost, ...prev]);
  };

  const handleLike = async (postId, shouldLike) => {
    if (shouldLike) {
      await feedAPI.likePost(postId);
      // Try to complete LIKE_POST task
      try {
        await tokensAPI.completeTask('LIKE_POST', { post_id: postId });
      } catch (err) {
        // Silently fail if task already completed
        console.log('Task completion failed:', err);
      }
    } else {
      await feedAPI.unlikePost(postId);
    }
  };

  const handleComment = async (postId) => {
    // TODO: Implement comment functionality
    console.log('Comment on post:', postId);
  };

  const handleReportOpen = (postId) => {
    setReportingPostId(postId);
    setReportModalOpen(true);
  };

  const handleReportSubmit = async (postId, reason) => {
    await feedAPI.reportPost(postId, reason);
  };

  const handleEdit = async (postId, newContent) => {
    try {
      const response = await feedAPI.editPost(postId, { content: newContent });
      setPosts((prev) => prev.map(p => p.id === postId ? { ...p, content: newContent } : p));
      toast.success('Post editado com sucesso');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao editar post');
      throw err;
    }
  };

  const handleDelete = async (postId, isAdminDelete) => {
    try {
      if (isAdminDelete) {
        await feedAPI.adminDeletePost(postId);
      } else {
        await feedAPI.deletePost(postId);
      }
      setPosts((prev) => prev.filter(p => p.id !== postId));
      toast.success('Post apagado com sucesso');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao apagar post');
      throw err;
    }
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadPosts(true);
    }
  };

  return (
    <PageTransition>
    <div className="max-w-5xl mx-auto" data-testid="feed-page">
      <div className="mb-5">
        <h1 className="text-2xl font-bold mb-1">Feed</h1>
        <p className="text-sm text-muted-foreground">
          Compartilhe sua jornada e conecte-se com a comunidade
        </p>
      </div>

      <div className="flex gap-6">
        {/* Center: Feed Column */}
        <div className="flex-1 min-w-0">
          {user && <FeedComposer onPostCreated={handleCreatePost} />}
          <FeedList
            posts={posts}
            loading={loading}
            error={error}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            onLike={handleLike}
            onComment={handleComment}
            onReport={handleReportOpen}
            onEdit={handleEdit}
            onDelete={handleDelete}
            currentUserId={user?.id}
            isAdmin={isAdmin}
          />
        </div>

        {/* Right: Widgets */}
        <aside className="hidden xl:block w-80 flex-shrink-0 space-y-3">
          {/* Card Falar com a ARIA */}
          <HoverCard
            className="rounded-2xl p-4 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/40 dark:to-purple-950/30 border border-violet-200 dark:border-violet-800/40 cursor-pointer"
            onClick={() => navigate('/aria')}
            data-testid="aria-widget-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-sm text-violet-900 dark:text-violet-200">Falar com a ARIA</h3>
                <p className="text-xs text-violet-600 dark:text-violet-400">Sua assistente terapêutica</p>
              </div>
              <ChevronRight className="h-4 w-4 text-violet-400" />
            </div>
          </HoverCard>

          {/* Card Atividades Diárias */}
          <HoverCard
            className="rounded-2xl p-4 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/40 dark:to-indigo-950/30 border border-purple-200 dark:border-purple-800/40 cursor-pointer"
            onClick={() => navigate('/atividades')}
            data-testid="daily-activities-card"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                <Flame className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-sm text-purple-900 dark:text-purple-200">Atividades Diárias</h3>
                <p className="text-xs text-purple-600 dark:text-purple-400">Complete e ganhe AWA</p>
              </div>
              <ChevronRight className="h-4 w-4 text-purple-400" />
            </div>
          </HoverCard>

          {/* Card Check-in Diário */}
          <CheckinCard />

          {/* Card Grupo de Mentores */}
          <MentorCard />

          {/* Card Sacola Virtual */}
          <VirtualBagCard />
        </aside>
      </div>

      <ReportModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        onSubmit={handleReportSubmit}
        postId={reportingPostId}
      />
    </div>
    </PageTransition>
  );
}
